import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { faThumbtack as faThumbtackSolid } from "@fortawesome/pro-solid-svg-icons";

import {
  INavigationDropdownOption,
  INavigationDropdownOptionSections,
  NavigationDropdownNew,
} from "components/navigationDropdownNew/NavigationDropdownNew";
import { IMergedReportEntry } from "pages/secure/shared/reports/reportCatalog.types";
import LockedReportCard from "./LockedReportCard";

interface IReportCardProps {
  report: IMergedReportEntry;
  isPinned: boolean;
  canSchedule: boolean;
  onOpen: () => void;
  onTogglePin: () => void;
  onScheduleReport?: () => void;
  onQuickExport?: () => void;
  onRunReport?: () => void;
}

export default function ReportCard(props: IReportCardProps) {
  const { report, isPinned, canSchedule, onOpen, onTogglePin, onScheduleReport, onQuickExport, onRunReport } = props;
  const { t } = useTranslation();

  const hasDescription = report.description.trim().length > 0;
  const compactModifier = hasDescription ? "" : " reports-list-card--compact";

  if (!report.hasPermission) {
    return (
      <LockedReportCard report={report} compactModifier={compactModifier} hasDescription={hasDescription} />
    );
  }

  const actions: INavigationDropdownOption[] = [
    {
      label: isPinned ? t("Unpin") : t("Pin"),
      icon: "thumbtack",
      onClick: onTogglePin,
    },
  ];

  if (onRunReport) {
    actions.push({
      label: t("Run report"),
      icon: "play",
      onClick: onRunReport,
    });
  }

  if (canSchedule && onScheduleReport) {
    actions.push({
      label: t("Schedule report"),
      icon: "clock",
      onClick: onScheduleReport,
    });
  }

  if (report.hasExport && onQuickExport) {
    actions.push({
      label: t("Quick export"),
      icon: "file-arrow-down",
      onClick: onQuickExport,
    });
  }

  const actionSections: INavigationDropdownOptionSections = [actions];

  return (
    <article className={`reports-list-card${compactModifier}`}>
      <button
        type="button"
        className="reports-list-card__hit-area"
        aria-label={t("Open {{title}}", { title: t(report.title) })}
        onClick={onOpen}
      />
      <div className={`reports-list-card__body${hasDescription ? "" : " reports-list-card__body--compact"}`}>
        <div className="reports-list-card__text">
          <span className="reports-list-card__title">{t(report.title)}</span>
          {hasDescription ? <span className="reports-list-card__description">{t(report.description)}</span> : null}
        </div>
        <div className="reports-list-card__actions">
          {isPinned ? (
            <button
              type="button"
              className="reports-list-card__pin-button"
              aria-label={t("Unpin {{title}}", { title: t(report.title) })}
              onClick={event => {
                event.stopPropagation();
                onTogglePin();
              }}
            >
              <FontAwesomeIcon className="reports-list-card__pin-icon" icon={faThumbtackSolid} />
            </button>
          ) : null}
          <div
            className="reports-list-card__dropdown"
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
          >
            <NavigationDropdownNew
              hideChevron
              showPlainTextLabel
              rightAlign
              label={<FontAwesomeIcon className="reports-list-card__action-icon" icon={faEllipsisVertical} />}
              sections={actionSections}
            />
          </div>
        </div>
      </div>
    </article>
  );
}
